import * as Yup from "yup"
import React, { useEffect } from "react"
import { observer } from "mobx-react-lite"
import { IconAlertCircle, IconGripVertical } from "@tabler/icons-react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import { Link, useNavigate, useParams } from "react-router-dom"
import { DatePicker } from "@mantine/dates"
import { useForm, yupResolver } from "@mantine/form"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  createStyles,
  Text,
  Group,
  LoadingOverlay,
  SimpleGrid,
  Stack,
  Textarea,
  TextInput,
  Title,
  Center,
  Flex, Anchor,
} from "@mantine/core"
import { PATHS } from "../../shared/lib/paths"
import { courseStore } from "../../app/store"
import { CourseService } from "../../app/services"
import {
  LandingColorSchemaSelector,
  LandingColorSchemaType,
  LandingSectionContainer,
  ImageDropzone,
  type Course,
  type LandingWhoIsTrainingFor,
} from "../../common"
import { InstructorSelectorMulti } from "../instructor"

const useStyles = createStyles((theme) => ({
  title: {
    paddingBottom: theme.spacing.md,
    marginBottom: `calc(${theme.spacing.md} * 1.5)`,
  },

  dropzoneWrapper: {
    minHeight: 330,
    position: "relative",
    flexGrow: 1,
  },

  whoIsTrainingForDropzoneWrapper: {
    width: 87,
    minWidth: 87,
    height: 87,
    position: "relative",
  },

  box: {
    padding: theme.spacing.xs,
    border: `1px dashed ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.md,
  },
}))

const schema = Yup.object().shape({
  name: Yup.string().required("Название курса обязательно"),
  cover: Yup.string().required("Загрузите обложку курса"),
  published: Yup.boolean().required(),
  meta: Yup.object().shape({
    title: Yup.string().required("Заголовок для SEO обязателен"),
    description: Yup.string().required("Описание курса для SEO обязательно"),
    keywords: Yup.string().required("Укажите ключевые слова"),
  }).required(),
  landing: Yup.object().shape({
    colorScheme: Yup.string().oneOf(Object.values(LandingColorSchemaType)).default(LandingColorSchemaType.BLUE_INDIGO).required(),
    startDate: Yup.string().required("Укажите дату начала"),
    duration: Yup.string().required("Укажите продолжительность курса"),
  }).required(),
})

export const CourseEdit = observer(function CourseEdit() {
  const { classes } = useStyles()
  const navigate = useNavigate()

  const { courseId } = useParams<{ courseId?: string }>()
  const { error, isLoading } = courseStore

  const form = useForm<Course>({
    validateInputOnChange: true,
    validate: yupResolver(schema),
    initialValues: {
      name: "",
      cover: undefined,
      published: true,
      meta: {
        title: "",
        description: "",
        keywords: "",
      },
      landing: {
        colorScheme: LandingColorSchemaType.BLUE_INDIGO,
        startDate: new Date(),
        duration: "6 месяцев",
        sections: {
          hero: {
            title: "",
            subtitle: "",
            image: undefined,
          },
          whoIsTrainingFor: {
            image: undefined,
            list: new Array(6).fill({ text: "", image: undefined }) as LandingWhoIsTrainingFor["list"],
          },
          team: [],
          trainingProgram: {
            description: "",
          },
          whatWillLearn: {
            list: [],
          },
          documents: {
            list: [],
          },
          learningProcess: {
            list: [],
          },
          landingPayment: {
            full: {
              price: 0,
              cover: undefined,
            },
            installment: {
              price: 0,
              cover: undefined,
            },
          },
          testDrive: {
            image: undefined,
            text: "",
            buttonLink: "",
          },
          faq: {
            list: [],
          },
        },
      },
    },
  })

  useEffect(() => {
    if (courseId) {
      void CourseService.getById(courseId).then((course) => {
        if (course) {
          const { updatedAt, createdAt, _id, ...rest } = course
          const preparedData: Course = {
            ...rest,
            landing: {
              ...rest.landing,
              startDate: new Date(rest.landing.startDate),
              sections: {
                ...rest.landing.sections,
                team: rest.landing.sections.team.map(el => el._id),
              },
            },
          }
          form.setValues(preparedData)
          form.resetDirty(preparedData)
        }
      })
    }

    return () => {
      form.reset()
    }
  }, [ courseId ])

  const whoIsTrainingForFields = form.values.landing.sections.whoIsTrainingFor.list.map((item, index) => (
    <Draggable key={index} index={index} draggableId={index.toString()}>
      {(provided) => (
        <Flex w="100%" align="center" gap={10} ref={provided.innerRef} {...provided.draggableProps}>
          <Center {...provided.dragHandleProps}>
            <IconGripVertical size="1.2rem"/>
          </Center>
          <Box className={classes.whoIsTrainingForDropzoneWrapper}>
            <ImageDropzone
              maxSize={0.3 * 1024 ** 2}
              size="extra-small"
              formValues={form.getInputProps(`landing.sections.whoIsTrainingFor.list.${index}.image`)}
            />
          </Box>
          <Textarea
            sx={{ flexGrow: 1 }}
            radius="md"
            placeholder="Описание особенности в 1-2 предложениях"
            {...form.getInputProps(`landing.sections.whoIsTrainingFor.list.${index}.text`)}
            minRows={3}
          />
        </Flex>
      )}
    </Draggable>
  ))

  const onSubmit = (values: typeof form.values): void => {
    const formData = new FormData()
  }

  return (
    <Box w="100%" pos="relative">
      <LoadingOverlay visible={isLoading} overlayBlur={2}/>

      <Title order={1} size={24} className={classes.title}>
        {courseId ? `Редактирование курса «${form.values.name}»` : "Добавление курса"}
      </Title>

      <form onSubmit={form.onSubmit(onSubmit)}>
        <Stack spacing="xl">

          <SimpleGrid
            cols={2}
            breakpoints={[
              { maxWidth: "md", cols: 1 },
            ]}
          >
            <Stack spacing="xl">
              <TextInput
                radius="md"
                label="Название курса"
                placeholder="Нутрициология"
                {...form.getInputProps("name")}
              />

              <Checkbox
                radius="md"
                label="Курс опубликован"
                {...form.getInputProps("published", { type: "checkbox" })}
              />

              <Box>
                <Text fz={18} fw={500}>Meta - информация о странице курса</Text>
                <Text c="dimmed" fz={14} mb={4}>Напрямую влияет на SEO продвижение странице в поиске</Text>

                <Stack spacing="xl" className={classes.box}>
                  <TextInput
                    radius="md"
                    label="Заголовок страницы"
                    placeholder="Нутрициология"
                    {...form.getInputProps("meta.title")}
                  />

                  <Textarea
                    radius="md"
                    label="Описание страницы"
                    description="Максимально подробное описание сути страницы"
                    {...form.getInputProps("meta.description")}
                    minRows={3}
                    autosize
                  />

                  <Textarea
                    radius="md"
                    label="Ключевые слова"
                    description="Перечистиле их через запятую"
                    placeholder=""
                    {...form.getInputProps("meta.keywords")}
                    minRows={3}
                    autosize
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack spacing={0}>
              <Text fz={14}>Основное изображение</Text>
              <Text c="dimmed" fz={12} mb={4}>Будет использовано в списке курсов</Text>
              <Box className={classes.dropzoneWrapper}>
                <ImageDropzone formValues={form.getInputProps("cover")}/>
              </Box>
            </Stack>
          </SimpleGrid>

          <SimpleGrid
            cols={4}
            breakpoints={[
              { maxWidth: "xl", cols: 3 },
              { maxWidth: "lg", cols: 2 },
              { maxWidth: "md", cols: 1 },
            ]}
          >
            <Stack>
              <TextInput
                radius="md"
                label="Продолжительность обучения"
                placeholder="6 месяцев"
                {...form.getInputProps("landing.duration")}
              />

              <LandingColorSchemaSelector
                radius="md"
                label="Цветовая схема"
                {...form.getInputProps("landing.colorScheme")}
              />
            </Stack>

            <Center>
              <Stack spacing={4}>
                <Text mb={4} fz={14} fw={500}>Дата старта курса</Text>
                <DatePicker
                  locale="ru"
                  {...form.getInputProps("landing.startDate")}
                />
              </Stack>
            </Center>
          </SimpleGrid>

          <LandingSectionContainer label="HERO">
            <SimpleGrid
              cols={2}
              breakpoints={[
                { maxWidth: "md", cols: 1 },
              ]}
            >
              <Stack spacing="xl">
                <TextInput
                  radius="md"
                  label="Заголовок"
                  placeholder="Нутрициология. Научный подход"
                  {...form.getInputProps("landing.sections.hero.title")}
                />
                <TextInput
                  radius="md"
                  label="Подзаголовок"
                  placeholder="Освойте востребованную профессию"
                  {...form.getInputProps("landing.sections.hero.subtitle")}
                />
              </Stack>

              <Stack spacing={0}>
                <Text fz={14}>Изображение</Text>
                <Box className={classes.dropzoneWrapper}>
                  <ImageDropzone formValues={form.getInputProps("landing.sections.hero.image")}/>
                </Box>
              </Stack>
            </SimpleGrid>
          </LandingSectionContainer>

          <LandingSectionContainer label="Это профессиональное обучение для вас, если">
            <SimpleGrid
              cols={2}
              breakpoints={[
                { maxWidth: "md", cols: 1 },
              ]}
            >
              <Box>
                <Text fz={14}>Список ключевых особенностей</Text>
                <Text c="dimmed" mb={4} fz={12}><Text span color="red">Важно! </Text>Для корректного отображения секции
                  размер всех иконок должен быть одинаковым. Рекомендуемый размер - 64 х 64 пикселов.</Text>
                <DragDropContext
                  onDragEnd={({ destination, source }) => {
                    if (destination?.index !== undefined && destination.index !== source.index) {
                      form.reorderListItem("landing.sections.whoIsTrainingFor.list", {
                        from: source.index,
                        to: destination.index,
                      })
                    }
                  }}
                >
                  <Droppable droppableId="dnd-list" direction="vertical">
                    {(provided) => (
                      <Stack spacing={10} w="100%" {...provided.droppableProps} ref={provided.innerRef}>
                        {whoIsTrainingForFields}
                        {provided.placeholder}
                      </Stack>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>

              <Stack spacing={0}>
                <Text fz={14}>Изображение</Text>
                <Box className={classes.dropzoneWrapper}>
                  <ImageDropzone formValues={form.getInputProps("landing.sections.hero.image")}/>
                </Box>
              </Stack>
            </SimpleGrid>
          </LandingSectionContainer>

          <LandingSectionContainer label="Преподавательский состав">
            <SimpleGrid
              cols={2}
              breakpoints={[
                { maxWidth: "md", cols: 1 },
              ]}
            >
              <InstructorSelectorMulti {...form.getInputProps("landing.sections.team")}/>

              <Text fz={14} my="auto">
                Добавить новых преподавателей в этой форме не получится. Нажмите&nbsp;
                <Anchor
                  fw={700}
                  href={PATHS.DASHBOARD.INSTRUCTORS.CREATE}
                  target="_blank"
                >
                  здесь
                </Anchor>
                &nbsp;, если хотите добавить нового члена команды, отсутствующего в предложенном списке.
              </Text>
            </SimpleGrid>
          </LandingSectionContainer>
        </Stack>

        {error && (
          <Alert mt="xl" icon={<IconAlertCircle size="1rem"/>} title="Произошла ошибка" color="red">
            {error}
          </Alert>
        )}

        <Group position="center" my="xl">
          <Button disabled={!form.isDirty() || !form.isValid()} type="submit">
            {courseId ? "Обновить" : "Создать"}
          </Button>
          <Button
            disabled={isLoading}
            variant="light"
            color="gray"
            component={Link}
            to={PATHS.DASHBOARD.TRAINING.COURSES.ROOT}
          >
            Назад
          </Button>
        </Group>
      </form>
    </Box>
  )
})
